'use client';

import {
  Box,
  Divider,
  List,
  ListItemButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { JumpLink } from '@/components/ContentBlocks/ContentBlocks';

interface JumpLinksSidebarProps {
  links: JumpLink[];
}

export function JumpLinksSidebar({ links }: JumpLinksSidebarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeId, setActiveId] = useState<string | null>(links[0]?.id ?? null);
  const _rafId = useRef<number | null>(null);

  const SCROLL_OFFSET = 110; // sticky header offset (px)
  const RETRY_INTERVAL = 200; // ms between DOM re-checks
  const MAX_RETRIES = 20; // ~4s max

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();

    const el = document.getElementById(id);
    if (!el) return;

    const y = el.getBoundingClientRect().top + window.scrollY - (SCROLL_OFFSET - 10);
    window.scrollTo({ top: y, behavior: 'smooth' });
    history.replaceState(null, '', `#${id}`);
  };

  useEffect(() => {
    if (!links.length) return;
    let retryCount = 0;
    let raf: number | null = null;
    let retryTimer: NodeJS.Timeout | null = null;

    const collectElements = () =>
      links.map((l) => document.getElementById(l.id)).filter(Boolean) as HTMLElement[];

    const updateActive = () => {
      const elements = collectElements();
      if (!elements.length) return;

      const scrollY = window.scrollY + SCROLL_OFFSET;
      let current: string | null = links[0]?.id ?? null;

      for (const el of elements) {
        if (el.offsetTop <= scrollY) current = el.id;
        else break;
      }

      setActiveId(current);
    };

    const onScroll = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        updateActive();
      });
    };

    const tryInit = () => {
      const elements = collectElements();
      if (elements.length === links.length || retryCount >= MAX_RETRIES) {
        updateActive();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return;
      }

      retryCount++;
      retryTimer = setTimeout(tryInit, RETRY_INTERVAL);
    };

    tryInit();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [links]);

  if (!links.length || isMobile) return null;

  return (
    <Box
      component="aside"
      data-component="JumpLinksSidebar"
      sx={{
        background: theme.palette.common.white,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        padding: 2,
        paddingLeft: 2.5,
        paddingBottom: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: 700,
          fontSize: { xs: 16, sm: 17 },
        }}
      >
        On this page
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Box
        sx={() => ({
          maxHeight: 'calc(100vh - 220px)',
          overflowY: 'auto',
        })}
      >
        <List dense disablePadding>
          {links.map((link) => {
            const isActive = activeId === link.id;
            return (
              <ListItemButton
                key={link.id}
                component="a"
                href={`#${link.id}`}
                onClick={(e) => handleClick(e, link.id)}
                className={isActive ? 'isActiveLink' : ''}
                sx={{
                  mb: '1.25rem',
                  '&:last-child': { mb: 0 },
                  fontSize: '0.9rem',
                  color: 'purple.700',
                  fontWeight: 400,
                  pl: 1,
                  pr: 0,
                  borderLeft: '3px solid transparent',
                  '&.isActiveLink': {
                    fontWeight: 600,
                    borderLeftColor: `${theme.palette.brand.primarySecondary1}`,
                    textDecoration: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.common.white,
                    },
                  },
                  '&:not(.isActiveLink):hover': {
                    borderLeftColor: `${theme.palette.brand.primarySecondary1}`,
                    backgroundColor: theme.palette.purple[100],
                    textDecoration: 'underline',
                  },
                }}
              >
                {link.label}
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}
